<script setup lang="ts">
import * as yup from "yup"
import { useOperationStore } from "~/stores/operation.store"
import { cloneDeep } from "lodash-es"
import type { components } from "~/assets/api/openapi"

const operationStore = useOperationStore()
const REASON_MAX_LENGTH = 1000

type ApplicationType = "EU" | "THIRD_PARTY"

const schema = yup.object().shape({
  documentsComplete: yup
    .boolean()
    .required("Pflichtfeld")
    .test(
      "is-complete",
      "Der Arbeitsschritt kann nur abgeschlossen werden wenn die Vollständigkeit der Unterlagen festgestellt wurde",
      yupTestOnlyOnCreate((value) => value === true)
    ),

  reason: yup.string().optional(),

  documentsCompleteDate: yup.string().when("documentsComplete", {
    is: true,
    then: (s) => s.required("Pflichtfeld"),
    otherwise: (s) => s.strip(),
  }),

  result: yup
    .string()
    .required("Pflichtfeld")
    .test(
      "is-not-open",
      "Der Arbeitsschritt kann nicht abgeschlossen werden, solange das Ergebnis nicht vorliegt",
      yupTestOnlyOnCreate((value) => value !== "open")
    ),

  dateDetermination: yup.string().when("result", {
    is: (v: string) => v && v !== "open",
    then: (s) => s.required("Pflichtfeld"),
    otherwise: (s) => s.strip(),
  }),
})

type Schema = yup.InferType<typeof schema>

const currentWorkStepData = computed<Schema>(
  () => (operationStore.currentWorkStep?.data ?? {}) as unknown as Schema
)

const applicationType = computed<ApplicationType | undefined>(() => {
  const textKey = operationStore.findFormDataValueByTextKey<
    components["schemas"]["LocaleValue"]
  >("gsb.ah.applicationDataFields.applicationType")?.textKey

  const lastPart = textKey?.split(".").pop()
  return lastPart as ApplicationType | undefined
})

const isReasonFieldEnabled = computed<boolean>(
  () => state.documentsComplete === true
)

const resultOptionsByApplicationType: Record<
  ApplicationType,
  IffRadioGroupOption[]
> = {
  EU: [
    {
      label: "Liegt noch nicht vor",
      value: "open",
    },
    {
      label:
        "Gleichwertigkeit gemäß Richtlinie automatische Anerkennung Sektorenberuf",
      value: "equivalenceSectoralProfessions",
    },
    {
      label: "Gleichwertigkeit aufgrund Konformitätsbescheinigung",
      value: "equivalenceCertificateConformity",
    },
    {
      label: "Gleichwertigkeit aufgrund erworbener Rechte",
      value: "equivalenceAcquiredRights",
    },
    {
      label: "Gleichwertigkeit festgestellt / Positiver Feststellungsbescheid",
      value: "equivalenceEstablished",
    },
    {
      label: "Nicht gleichwertig / Negativer Feststellungsbescheid",
      value: "notEquivalent",
    },
  ],
  THIRD_PARTY: [
    {
      label: "Liegt noch nicht vor",
      value: "open",
    },
    {
      label: "Gleichwertigkeit festgestellt / Positiver Feststellungsbescheid",
      value: "equivalenceEstablished",
    },
    {
      label: "Nicht gleichwertig / Negativer Feststellungsbescheid",
      value: "notEquivalent",
    },
  ],
}

const validResultsByApplicationType: Record<ApplicationType, string[]> = {
  EU: [
    "equivalenceSectoralProfessions",
    "equivalenceCertificateConformity",
    "equivalenceAcquiredRights",
    "equivalenceEstablished",
    "notEquivalent",
  ],
  THIRD_PARTY: ["equivalenceEstablished", "notEquivalent"],
}

const state = reactive<Schema>(cloneDeep(currentWorkStepData.value))

watch(
  () => state.documentsComplete,
  (value) => {
    if (value === false) {
      state.documentsCompleteDate = undefined
    }
    operationStore.unsavedChanges = false
  },
  { immediate: true }
)

watch(isReasonFieldEnabled, (isEnabled) => {
  if (!isEnabled) {
    state.reason = undefined
  }
})

watch(
  () => state.result,
  (value) => {
    if (value === "open") {
      state.dateDetermination = undefined
    }
  }
)

watch(applicationType, () => {
  if (
    applicationType.value &&
    state.result &&
    !validResultsByApplicationType[applicationType.value].includes(
      state.result
    ) &&
    state.result !== "open"
  ) {
    state.result = ""
  }
})
</script>

<template>
  <IffOperationWorkStepFormV2 :state="state" :schema="schema">
    <template #above-form>
      <IffReviewsSection
        v-if="operationStore.currentWorkStep"
        :work-step-id="operationStore.currentWorkStep?.id"
        additional-headline="Prüfung Gleichwertigkeit"
      />
    </template>

    <IffContentSection
      headline="Vollständigkeit feststellen"
      :is-last-child="false"
    >
      <IffDualRadioGroup
        legend="Sind die Unterlagen vollständig und formal korrekt, um die Gleichwertigkeit festzustellen?"
        name="documentsComplete"
        v-model="state.documentsComplete"
        required
        aria-controls="documents-completion-date-region"
      />
      <div id="documents-completion-date-region" aria-live="polite">
        <IffTransitionHeight>
          <UFormGroup
            name="documentsCompleteDate"
            label="Fristbeginn"
            v-if="state.documentsComplete"
            required
            class="max-w-[150px]"
            aria-controls="reason-region"
          >
            <template #hint>
              <IffIconTooltip
                text="
                BQFG: Wert wird in Datum der Vollständigkeit der vorzulegenden Unterlagen (e07) übernommen.

                Frist zur Bearbeitung wird wie folgt berechnet:
          - Fristbeginn plus 3 Monate bei Apothekern, Ärzten und Zahnärzten, sowie plus 4 Monate bei Psychotherapeuten.
          - Bei Anträgen im Rahmen des FEG: plus 2 Monate für alle Berufsgruppen."
              />
              <UButton
                @click="
                  state.documentsCompleteDate = new Date()
                    .toISOString()
                    .slice(0, 10)
                "
                icon="i-ic-outline-access-time"
                size="sm"
                color="gray"
                variant="ghost"
                :trailing="false"
                aria-label="Aktuelles Datum wählen"
                title="Aktuelles Datum wählen"
              />
            </template>

            <UInput
              v-model="state.documentsCompleteDate"
              type="date"
              aria-required="true"
              class="whitespace-normal max-w-[150px]"
            />
          </UFormGroup>
        </IffTransitionHeight>
      </div>
      <div id="reason-region" aria-live="polite">
        <IffTransitionHeight>
          <UFormGroup
            v-if="isReasonFieldEnabled"
            class="w-[600px]"
            name="reason"
            label="Begründung"
            :help="`Noch ${
              REASON_MAX_LENGTH - (state.reason?.length ?? 0)
            } Zeichen verfügbar`"
          >
            <UTextarea
              v-model="state.reason"
              :maxlength="REASON_MAX_LENGTH"
              resize
            />
          </UFormGroup>
        </IffTransitionHeight>
      </div>
    </IffContentSection>
    <IffContentSection
      headline="Abschließendes Ergebnis der Gleichwertigkeitsprüfung"
      :is-last-child="true"
    >
      <IffRadioGroup
        v-if="applicationType"
        legend="Ergebnis"
        name="result"
        v-model="state.result"
        :options="resultOptionsByApplicationType[applicationType]"
        required
        aria-controls="determination-date-region"
      />
      <div id="determination-date-region" aria-live="polite">
        <IffTransitionHeight>
          <UFormGroup
            v-if="state.result && state.result !== 'open'"
            name="dateDetermination"
            label="Datum der Feststellung"
            required
            class="max-w-[240px]"
          >
            <template #hint>
              <IffIconTooltip
                text="BQFG: Wert wird in Datum der Entscheidung (e08) übernommen."
              />

              <UButton
                @click="
                  state.dateDetermination = new Date()
                    .toISOString()
                    .slice(0, 10)
                "
                icon="i-ic-outline-access-time"
                size="sm"
                color="gray"
                variant="ghost"
                :trailing="false"
                aria-label="Aktuelles Datum wählen"
                title="Aktuelles Datum wählen"
              />
            </template>

            <UInput
              v-model="state.dateDetermination"
              type="date"
              aria-required="true"
              class="whitespace-normal max-w-[150px]"
            />
          </UFormGroup>
        </IffTransitionHeight>
      </div>
    </IffContentSection>
  </IffOperationWorkStepFormV2>
</template>
